import _extends from "@babel/runtime-corejs3/helpers/esm/extends";
import React from "react";
import PropTypes from "prop-types";
import { FormBlockColumn } from "@xo-union/tk-component-form-grid";
import { Checkbox } from "@xo-union/tk-component-switches";
import { useTrackImpression } from "@xo-union/tk-component-analytics";
import { Form, Field } from "@xo-union/tk-component-fields";
import { compose } from "@xo-union/react-css-modules";
import { NewWindowAnchor } from "@xo-union/component-standard-elements";
import { ModalHeader } from "@xo-union/ui-accessibility";
import BlankButton from "@xo-union/component-blank-button";
import { InlineAlert } from "@xo-union/tk-component-alerts";
import FormSubmitButton from "../Buttons/index.js";
import styles from "../../css/index.css";
import mapPropsToAnalyticsData from "./mapPropsToAnalyticsData/index.js";
import EmailFlags from "./EmailFlags.js";
import CanSpam from "../CanSpam/index.js";
import generateFormLinks from "../../utils/generateFormLinks/index.js";

const FormFooter = _ref => {
  let {
    onClickLogIn
  } = _ref;
  return /*#__PURE__*/React.createElement("div", {
    className: styles.footer
  }, "Already a member? \xA0", /*#__PURE__*/React.createElement(BlankButton, {
    className: styles.footerLink,
    onClick: onClickLogIn
  }, "Log In"), /*#__PURE__*/React.createElement("hr", null), "Are you a vendor? \xA0", /*#__PURE__*/React.createElement("a", {
    className: styles.footerLink,
    href: "https://www.theknot.com/vendors/home?itm_source=theknot.com&itm_medium=referral&itm_campaign=sign-up-modal"
  }, "Start here"));
};

process.env.NODE_ENV !== "production" ? FormFooter.propTypes = {
  onClickLogIn: PropTypes.func.isRequired
} : void 0;

const SignUpForm = props => {
  const {
    Footer,
    header,
    Header,
    inProgress,
    initialFocusRef,
    metadata,
    onClickLogIn,
    onSubmit,
    subHeader,
    emailProps,
    passwordProps,
    alertProps
  } = props;
  const [emailOptIn, setEmailOptin] = React.useState(null);
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const toggleOptIn = React.useCallback(e => {
    setEmailOptin(e.target.checked);
  }, []);
  useTrackImpression(mapPropsToAnalyticsData(props));
  const links = generateFormLinks();
  return /*#__PURE__*/React.createElement(CanSpam, null, _ref2 => {
    let {
      requireExplicitOptin,
      ready
    } = _ref2;
    const finalEmailOptinValue = emailOptIn === null ? !requireExplicitOptin : emailOptIn;

    const handleSubmit = (evt, originalEvent) => {
      onSubmit({ ...evt,
        data: {
          email,
          password,
          brand: 'theknot',
          action: metadata.userAction,
          application: metadata.applicationName,
          optins: finalEmailOptinValue ? EmailFlags.yes : EmailFlags.no
        }
      }, originalEvent);
    };

    if (!ready) {
      return null;
    }

    return /*#__PURE__*/React.createElement("div", {
      className: styles.formContainer
    }, Header ? /*#__PURE__*/React.createElement(Header, null) : /*#__PURE__*/React.createElement(React.Fragment, null, header && /*#__PURE__*/React.createElement(ModalHeader, {
      className: styles.header
    }, header), subHeader && /*#__PURE__*/React.createElement("p", {
      className: styles.subHeader
    }, subHeader)), /*#__PURE__*/React.createElement(InlineAlert, _extends({
      classes: compose({
        container: styles.formAlert
      }),
      type: "error",
      visible: false
    }, alertProps), typeof (alertProps === null || alertProps === void 0 ? void 0 : alertProps.children) === 'function' ? alertProps.children(alertProps.data) : (alertProps === null || alertProps === void 0 ? void 0 : alertProps.data) || 'Something went wrong. Please try again.'), /*#__PURE__*/React.createElement(Form, {
      onSubmit: handleSubmit,
      "aria-busy": inProgress ? 'true' : undefined,
      "aria-label": "Create account"
    }, /*#__PURE__*/React.createElement(FormBlockColumn, null, /*#__PURE__*/React.createElement(Field, _extends({
      value: email,
      onChange: evt => setEmail(evt.target.value),
      validations: {
        email: true,
        required: true
      },
      type: "email",
      autoComplete: "email",
      name: "email",
      label: "Email Address",
      ref: initialFocusRef
    }, emailProps))), /*#__PURE__*/React.createElement(FormBlockColumn, null, /*#__PURE__*/React.createElement(Field, _extends({
      value: password,
      onChange: evt => setPassword(evt.target.value),
      name: "password",
      type: "password",
      label: "Password",
      autoComplete: "new-password"
      /* Validation props */
      ,
      validations: {
        required: true,
        minLength: 6
      },
      subTextOnValid: "Password is valid"
    }, passwordProps))), /*#__PURE__*/React.createElement("div", {
      className: styles.formSubText
    }, /*#__PURE__*/React.createElement("small", null, "By clicking \u2018Sign Up\u2019, I agree to The Knot's\xA0", /*#__PURE__*/React.createElement(NewWindowAnchor, {
      href: links.PRIVACY_POLICY
    }, "Privacy Policy"), ' ', "and\xA0", /*#__PURE__*/React.createElement(NewWindowAnchor, {
      href: links.TERMS_AND_CONDITIONS
    }, "Terms of Use"), ". I understand The Knot may share my information with partners as described\xA0", /*#__PURE__*/React.createElement(NewWindowAnchor, {
      href: links.PRIVACY_POLICY
    }, "here"), ".")), /*#__PURE__*/React.createElement(FormSubmitButton, {
      isLoading: inProgress
    }, "Sign Up"), requireExplicitOptin && /*#__PURE__*/React.createElement("div", {
      className: styles.emailOptinContainer
    }, /*#__PURE__*/React.createElement("div", {
      className: styles.emailOptinCenter
    }, /*#__PURE__*/React.createElement(Checkbox, {
      checked: finalEmailOptinValue,
      onChange: toggleOptIn,
      name: "emailOptIn",
      value: "true"
    }, "Send me wedding ideas, tips, and special offers!")))), /*#__PURE__*/React.createElement(Footer, {
      onClickLogIn: onClickLogIn
    }));
  });
};

process.env.NODE_ENV !== "production" ? SignUpForm.propTypes = {
  /**
   * Will be called when clicking on CTA to switch to log in view
   */
  onClickLogIn: PropTypes.func.isRequired,

  /**
   * Form submission callback
   */
  onSubmit: PropTypes.func.isRequired,

  /**
   * Used to display loading CTA
   */
  inProgress: PropTypes.bool,

  /**
   * For analytics
   */
  metadata: PropTypes.shape({
    applicationName: PropTypes.string,
    userAction: PropTypes.string
  }).isRequired,

  /**
   * Used to override the header copy, while preserving the current subHeader
   */
  header: PropTypes.node,

  /**
   * Used to override the subHeader copy, while preserving the header
   */
  subHeader: PropTypes.node,

  /**
   * Used to override the footer section
   */
  Footer: PropTypes.func,

  /**
   * Used to override the header section (replaces the header and subHeader)
   */
  Header: PropTypes.func,

  /**
   * Element to set as the initial focus
   */
  initialFocusRef: PropTypes.shape({
    current: PropTypes.shape()
  }),

  /**
   * Used to set additional props on the email field
   */
  emailProps: PropTypes.shape(),

  /**
   * Used to set additional props on the password field
   */
  passwordProps: PropTypes.shape(),

  /**
   * Used to set additional props on the alert
   */
  alertProps: PropTypes.shape()
} : void 0;
SignUpForm.defaultProps = {
  inProgress: false,
  header: 'Your Personal Wedding Plan Starts Now',
  subHeader: '',
  Footer: FormFooter,
  Header: null,
  emailProps: {},
  passwordProps: {}
};
export default SignUpForm;